// src/pages/admin/ProjectMembersPanel.jsx
import { useEffect, useMemo, useState } from "react";
import {
  RefreshCcw,
  X,
  AlertCircle,
  UserMinus,
  Loader2,
} from "lucide-react";
import { fetchWithAuth } from "../../api/fetchWithAuth";
import {
  PROJECT_ROLE_OPTIONS,
  DEFAULT_PROJECT_INVITE_ROLE,
} from "../../constants/projectRoles";

/**
 * Members panel for a single project. Handles: list members, list pending
 * invites, invite new member, resend invite, revoke invite, remove
 * member, change member role.
 *
 * Props:
 *   projectId      — required, scopes all API calls
 *   onMembersChange — optional callback fired after any mutation; used by
 *                    the parent to refresh adjacent state (e.g. license
 *                    bar if we ever gate seats per-project)
 *
 * Permission model: server enforces org_admin OR project_owner on every
 * route this panel hits. Client doesn't gate — if the user got to this
 * page they can see the panel; attempting an action will 403 cleanly if
 * they lack permission. Keeps the component simple and defensive.
 */
export default function ProjectMembersPanel({ projectId, onMembersChange }) {
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);

  const [email, setEmail] = useState("");
  const [role, setRole] = useState(DEFAULT_PROJECT_INVITE_ROLE);
  const [inviting, setInviting] = useState(false);
  const [formError, setFormError] = useState(null);

  // Per-row error surface: { id, kind: 'member'|'invite', message }
  // Keyed by entity id so we can show the error on the right row.
  const [rowError, setRowError] = useState(null);

  // Track owner count so we can disable the role-change / remove
  // actions on the LAST owner proactively (the backend also rejects,
  // this is just cheaper UX than round-tripping).
  const ownerCount = useMemo(
    () => members.filter((m) => m.role === "owner").length,
    [members]
  );

  async function load() {
    setLoading(true);
    try {
      const [mRes, iRes] = await Promise.all([
        fetchWithAuth(`/api/v1/admin/projects/${projectId}/members`),
        fetchWithAuth(`/api/v1/admin/projects/${projectId}/invites`),
      ]);

      if (mRes.ok) {
        setMembers(await mRes.json());
      } else {
        console.error("[project-members] list failed", mRes.status);
      }

      if (iRes.ok) {
        setInvites(await iRes.json());
      } else {
        console.error("[project-invites] list failed", iRes.status);
      }
    } catch (e) {
      console.error("[project-members] load error", e);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (projectId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  function notify() {
    if (typeof onMembersChange === "function") {
      Promise.resolve(onMembersChange()).catch((err) =>
        console.error("[project-members] onMembersChange callback failed", err)
      );
    }
  }

  // ── Invite ────────────────────────────────────────────────────────

  async function sendInvite() {
    const trimmed = email.trim();
    if (!trimmed) {
      setFormError("Enter an email address");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setFormError("That doesn't look like a valid email");
      return;
    }

    setFormError(null);
    setInviting(true);

    try {
      const res = await fetchWithAuth(
        `/api/v1/admin/projects/${projectId}/invites`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: trimmed.toLowerCase(), role }),
        }
      );

      if (!res.ok) {
        let msg = `Invite failed (${res.status})`;
        try {
          const b = await res.json();
          if (b?.error) msg = b.error;
        } catch {
          /* not JSON */
        }
        setFormError(msg);
        return;
      }

      // Backend returns either {status:'added'} (existing user, direct
      // add to project) or {status:'invited'} (new user, email sent).
      // We refresh both lists regardless — cheap, covers both cases.
      setEmail("");
      setRole(DEFAULT_PROJECT_INVITE_ROLE);
      await load();
      notify();
    } catch (e) {
      console.error("[project-invites] create error", e);
      setFormError("Network error. Try again.");
    } finally {
      setInviting(false);
    }
  }

  // ── Invite actions ────────────────────────────────────────────────

  async function resendInvite(inviteId) {
    setRowError(null);
    try {
      const res = await fetchWithAuth(
        `/api/v1/admin/invites/${inviteId}/resend`,
        { method: "POST" }
      );
      if (!res.ok) {
        setRowError({
          id: inviteId,
          kind: "invite",
          message: `Resend failed (${res.status})`,
        });
        return;
      }
      await load();
    } catch (e) {
      console.error("[project-invites] resend error", e);
      setRowError({ id: inviteId, kind: "invite", message: "Network error" });
    }
  }

  async function revokeInvite(inviteId) {
    setRowError(null);
    try {
      const res = await fetchWithAuth(
        `/api/v1/admin/invites/${inviteId}/revoke`,
        { method: "POST" }
      );
      if (!res.ok) {
        setRowError({
          id: inviteId,
          kind: "invite",
          message: `Revoke failed (${res.status})`,
        });
        return;
      }
      await load();
      notify();
    } catch (e) {
      console.error("[project-invites] revoke error", e);
      setRowError({ id: inviteId, kind: "invite", message: "Network error" });
    }
  }

  // ── Member actions ────────────────────────────────────────────────

  async function changeMemberRole(userId, newRole, currentRole) {
    if (newRole === currentRole) return;

    // Client-side guard: can't demote the last owner. Server rejects
    // this too (via "Cannot remove last project owner" logic on delete,
    // but role-change currently has no equivalent guard — flagged
    // below as a backend todo).
    if (currentRole === "owner" && newRole !== "owner" && ownerCount <= 1) {
      setRowError({
        id: userId,
        kind: "member",
        message: "Can't change role — at least one owner is required",
      });
      return;
    }

    setRowError(null);
    try {
      const res = await fetchWithAuth(
        `/api/v1/admin/projects/${projectId}/members/${userId}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ role: newRole }),
        }
      );
      if (!res.ok) {
        let msg = `Role update failed (${res.status})`;
        try {
          const b = await res.json();
          if (b?.error) msg = b.error;
        } catch { /* ignore */ }
        setRowError({ id: userId, kind: "member", message: msg });
        return;
      }
      await load();
    } catch (e) {
      console.error("[project-members] role-change error", e);
      setRowError({ id: userId, kind: "member", message: "Network error" });
    }
  }

  async function removeMember(userId, currentRole) {
    // Client-side guard matching the backend's.
    if (currentRole === "owner" && ownerCount <= 1) {
      setRowError({
        id: userId,
        kind: "member",
        message: "Can't remove — at least one owner is required",
      });
      return;
    }

    setRowError(null);
    try {
      const res = await fetchWithAuth(
        `/api/v1/admin/projects/${projectId}/members/${userId}`,
        { method: "DELETE" }
      );
      if (!res.ok) {
        let msg = `Remove failed (${res.status})`;
        try {
          const b = await res.json();
          if (b?.error) msg = b.error;
        } catch { /* ignore */ }
        setRowError({ id: userId, kind: "member", message: msg });
        return;
      }
      await load();
      notify();
    } catch (e) {
      console.error("[project-members] remove error", e);
      setRowError({ id: userId, kind: "member", message: "Network error" });
    }
  }

  // ── Render ────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex justify-center py-6">
        <Loader2 className="animate-spin text-gray-400" size={18} />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Invite form */}
      <div className="space-y-2">
        <h3 className="text-sm font-semibold text-gray-200">Invite a member</h3>

        <div className="flex gap-2">
          <input
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setFormError(null);
            }}
            placeholder="user@email.com"
            disabled={inviting}
            className="flex-1 bg-bg border border-slate-700 rounded px-2 py-1 text-xs disabled:opacity-50"
          />

          <select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            disabled={inviting}
            className="bg-bg border border-slate-700 rounded px-2 py-1 text-xs disabled:opacity-50"
          >
            {PROJECT_ROLE_OPTIONS.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>

          <button
            disabled={inviting}
            onClick={sendInvite}
            className="px-3 py-1 rounded bg-accent-action/30 text-xs hover:bg-accent-action/50
                       disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Invite
          </button>
        </div>

        {formError && (
          <div className="flex items-center gap-1.5 text-xs text-red-400">
            <AlertCircle size={12} />
            {formError}
          </div>
        )}
      </div>

      {/* Members list */}
      <div>
        <h3 className="text-sm font-semibold text-gray-200 mb-2">
          Members
          <span className="ml-2 text-xs font-normal text-fg-secondary">
            ({members.length})
          </span>
        </h3>

        <div className="divide-y divide-slate-800">
          {members.map((m) => {
            const hasError =
              rowError?.id === m.user_id && rowError.kind === "member";
            const isLastOwner = m.role === "owner" && ownerCount <= 1;

            return (
              <div
                key={m.user_id}
                className="flex items-center justify-between py-2 text-xs gap-3"
              >
                <div className="min-w-0 flex-1">
                  <div className="text-gray-200 truncate">{m.email}</div>
                  {hasError && (
                    <div className="text-red-400 mt-0.5">{rowError.message}</div>
                  )}
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  <select
                    value={m.role}
                    onChange={(e) =>
                      changeMemberRole(m.user_id, e.target.value, m.role)
                    }
                    className="bg-bg border border-slate-700 rounded px-2 py-0.5 text-xs"
                    title={
                      isLastOwner
                        ? "Can't change — at least one owner is required"
                        : undefined
                    }
                  >
                    {PROJECT_ROLE_OPTIONS.map((r) => (
                      <option key={r.value} value={r.value}>
                        {r.label}
                      </option>
                    ))}
                  </select>

                  <button
                    onClick={() => removeMember(m.user_id, m.role)}
                    disabled={isLastOwner}
                    className="text-gray-400 hover:text-red-400 transition
                               disabled:opacity-40 disabled:cursor-not-allowed"
                    title={
                      isLastOwner
                        ? "Can't remove — at least one owner is required"
                        : "Remove from project"
                    }
                  >
                    <UserMinus size={14} />
                  </button>
                </div>
              </div>
            );
          })}

          {!members.length && (
            <div className="text-fg-secondary text-xs py-2">No members</div>
          )}
        </div>
      </div>

      {/* Pending invites list */}
      <div>
        <h3 className="text-sm font-semibold text-gray-200 mb-2">
          Pending Invitations
          {invites.length > 0 && (
            <span className="ml-2 text-xs font-normal text-fg-secondary">
              ({invites.length})
            </span>
          )}
        </h3>

        <div className="divide-y divide-slate-800">
          {invites.map((invite) => {
            const hasError =
              rowError?.id === invite.id && rowError.kind === "invite";
            return (
              <div
                key={invite.id}
                className="flex items-center justify-between py-2 text-xs gap-3"
              >
                <div className="min-w-0 flex-1">
                  <div className="text-gray-200 truncate">{invite.email}</div>
                  <div className="text-fg-secondary font-mono uppercase tracking-wider">
                    {invite.role}
                  </div>
                  {hasError && (
                    <div className="text-red-400 mt-0.5">{rowError.message}</div>
                  )}
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  {!invite.accepted_at && !invite.revoked_at && (
                    <>
                      <button
                        onClick={() => resendInvite(invite.id)}
                        className="text-gray-400 hover:text-accent-action"
                        title="Resend invite"
                      >
                        <RefreshCcw size={14} />
                      </button>

                      <button
                        onClick={() => revokeInvite(invite.id)}
                        className="text-gray-400 hover:text-red-400"
                        title="Revoke invite"
                      >
                        <X size={14} />
                      </button>
                    </>
                  )}

                  {invite.accepted_at && (
                    <span className="text-[10px] text-green-400 font-mono uppercase tracking-wider">
                      Accepted
                    </span>
                  )}

                  {invite.revoked_at && (
                    <span className="text-[10px] text-fg-secondary font-mono uppercase tracking-wider">
                      Revoked
                    </span>
                  )}
                </div>
              </div>
            );
          })}

          {!invites.length && (
            <div className="text-fg-secondary text-xs py-2">
              No pending invites
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
